import { create } from 'zustand';
import { MODES, type Asset } from '@scio3/shared-types';

/** Subset of MODES used in asset list: default browsing, shared folders, or cloud drive view */
type AssetListMode = typeof MODES.DEFAULT | typeof MODES.SHARE_WITH_ME | typeof MODES.DRIVE;

interface AssetFilterState {
  fileType: string[];
  tags: string[];
  searchText: string;
  searchScope: 'all' | 'folder';
  /** Navigation mode: SHARE_WITH_ME shows shared folders, DRIVE browses cloud storage asset children */
  mode: AssetListMode;
  /** Root cloud storage asset when in DRIVE mode (Google Drive, OneDrive, Dropbox) */
  driveAsset: Asset | null;
  /** Path before entering DRIVE mode — restored on Close */
  preDrivePath: string | null;

  setFileType: (fileType: string[]) => void;
  setTags: (tags: string[]) => void;
  setSearchText: (text: string) => void;
  setSearchScope: (scope: 'all' | 'folder') => void;
  setMode: (mode: AssetListMode) => void;
  clearFilters: () => void;
  hasActiveFilters: () => boolean;
  /** Enter DRIVE mode: browse child assets of a cloud storage asset */
  enterDriveMode: (asset: Asset, currentPath: string) => void;
  /** Exit DRIVE mode: returns previous path for navigation */
  exitDriveMode: () => { previousPath: string };
}

export const useAssetFilterStore = create<AssetFilterState>((set, get) => ({
  fileType: [],
  tags: [],
  searchText: '',
  searchScope: 'all',
  mode: MODES.DEFAULT,
  driveAsset: null,
  preDrivePath: null,

  setFileType: (fileType) => set({ fileType, mode: MODES.DEFAULT }),
  setTags: (tags) => set({ tags }),
  setSearchText: (text) => {
    if (get().searchText !== text) {
      // Non-empty search exits SHARE_WITH_ME mode (mirrors setFileType behavior).
      // Empty string intentionally does NOT restore SHARE_WITH_ME — that requires
      // an explicit setMode(MODES.SHARE_WITH_ME) call.
      set({ searchText: text, ...(text ? { mode: MODES.DEFAULT } : {}) });
    }
  },
  setSearchScope: (scope) => {
    if (get().searchScope !== scope) set({ searchScope: scope });
  },
  setMode: (mode) =>
    set({ mode, fileType: [], tags: [], searchText: '', driveAsset: null, preDrivePath: null }),
  clearFilters: () =>
    set({
      fileType: [],
      tags: [],
      searchText: '',
      mode: MODES.DEFAULT,
      driveAsset: null,
      preDrivePath: null,
    }),
  hasActiveFilters: () => {
    const s = get();
    return (
      s.fileType.length > 0 ||
      s.tags.length > 0 ||
      s.searchText.length > 0 ||
      s.mode === MODES.SHARE_WITH_ME
    );
  },
  enterDriveMode: (asset, currentPath) =>
    set({
      mode: MODES.DRIVE,
      driveAsset: asset,
      preDrivePath: currentPath,
      searchText: '',
      fileType: [],
      tags: [],
    }),
  exitDriveMode: () => {
    const { preDrivePath } = get();
    set({
      mode: MODES.DEFAULT,
      driveAsset: null,
      preDrivePath: null,
    });
    return { previousPath: preDrivePath ?? '/' };
  },
}));
